import React, { useCallback, useEffect, useMemo } from "react";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { StackScreenProps } from "@react-navigation/stack";
import VStack from "../../ui_atoms/VStack";
import auth from "../../contexts/FirebaseAuth";
import { useI18n } from "../../hooks/i18n-hooks";
import ListItem from "../../ui_atoms/ListItem";
import Loading from "../../components/Loading";
import { useTheme } from "../../hooks/use-theme";
import ScreenCard from "../ScreenCard";
import { usePostOnboardingData } from "../../hooks/use-postonboarding-data";
import HeaderActions from "../../components/HeaderActions";
import { StyleSheet } from "react-native";
import { useIntercom } from "../../hooks/intercom";
import { useDeleteAccount } from "../../hooks/use-delete-account";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { useDeleteCurrentProfile } from "../../hooks/use-delete-current-profile";
import { ProfileType } from "../../__generated__/graphql";
import { MenuAction } from "../user/ScreenHeader";

type PostOnboardingProfileEditorScreenProps = StackScreenProps<
    RootStackParamList,
    "PostOnboardingProfileEditor"
>;

const PostOnboardingProfileEditorScreen = ({
    navigation,
    route,
}: PostOnboardingProfileEditorScreenProps) => {
    const { newProfileId } = route.params || {};

    const i18n = useI18n();
    const styles = useStyles();
    const { colors } = useTheme();
    const { setProfile } = React.useContext(ProfileSwitcherManagerContext);
    const { user } = React.useContext(AuthManagerContext);
    const { presentIntercom } = useIntercom();
    const onDeleteAccount = useDeleteAccount();
    const onDeleteWorkerProfile = useDeleteCurrentProfile({
        profileId: newProfileId,
        profileType: ProfileType.Worker,
    });

    const deleteWorkerProfile = useCallback(async () => {
        try {
            await onDeleteWorkerProfile();
            navigation.goBack();
        } catch (e) {
            /* empty */
        }
    }, [navigation, onDeleteWorkerProfile]);

    const editCivilState = useCallback(() => {
        navigation.navigate(
            "CivilStateEdit",
            newProfileId ? { profileId: newProfileId } : undefined,
        );
    }, [navigation, newProfileId]);

    const editFiscalAttestation = useCallback(() => {
        navigation.navigate(
            "FiscalAttestationEdit",
            newProfileId ? { profileId: newProfileId } : undefined,
        );
    }, [navigation, newProfileId]);

    const editPrivateEmail = useCallback(() => {
        navigation.navigate(
            "PrivateEmailEdit",
            newProfileId ? { profileId: newProfileId } : undefined,
        );
    }, [navigation, newProfileId]);

    const editHomeAddress = useCallback(() => {
        navigation.navigate(
            "HomeAddressEdit",
            newProfileId ? { profileId: newProfileId } : undefined,
        );
    }, [navigation, newProfileId]);

    const editChildren = useCallback(() => {
        navigation.navigate("ChildrenEdit", newProfileId ? { profileId: newProfileId } : undefined);
    }, [navigation, newProfileId]);

    const editBankAccount = useCallback(() => {
        navigation.navigate("BankAccountEditModal", { profileId: newProfileId });
    }, [navigation, newProfileId]);

    const {
        isValid,
        maritalStatus,
        bankAccount,
        children,
        hasChildren,
        isPartnerAndChildrenDataRequired,
        isFiscalAttestationRequired,
        hasFiscalAttestation,
        email,
        address,
        avsNumber,
    } = usePostOnboardingData();

    const next = useCallback(async () => {
        if (newProfileId) {
            if (user?.workerProfile) {
                await setProfile(user?.workerProfile);
            }
            navigation.navigate("Home", { screen: "ProfileStack" });
        } else {
            navigation.navigate("SignupFinished");
        }
    }, [navigation, newProfileId, setProfile, user?.workerProfile]);

    useEffect(() => {
        navigation.setOptions({
            headerRight: () => {
                return (
                    <HeaderActions
                        actions={[
                            {
                                label: i18n.t("assistance"),
                                icon: "assistance",
                                onPress: () => {
                                    void presentIntercom();
                                },
                            },
                            ...((newProfileId
                                ? [
                                      {
                                          label: i18n.t("profile.delete.worker.profile"),
                                          icon: "trash",
                                          onPress: () => {
                                              void deleteWorkerProfile();
                                          },
                                          color: colors.error,
                                      },
                                  ]
                                : [
                                      {
                                          label: i18n.t("profile.logout_title"),
                                          icon: "logout",
                                          onPress: () => {
                                              void auth().signOut();
                                          },
                                      },
                                      {
                                          label: i18n.t("profile.delete_account_title"),
                                          icon: "trash",
                                          onPress: onDeleteAccount,
                                          color: colors.error,
                                      },
                                  ]) as MenuAction[]),
                        ]}
                        style={styles.headerActions}
                    />
                );
            },
            ...(newProfileId
                ? {
                      headerTitle: i18n.t("screens.profile_editor.new_profile"),
                  }
                : {}),
        });
    }, [
        colors.error,
        deleteWorkerProfile,
        i18n,
        navigation,
        newProfileId,
        onDeleteAccount,
        onDeleteWorkerProfile,
        presentIntercom,
        styles.headerActions,
    ]);

    if (!user) {
        return (
            <VStack>
                <Loading />
            </VStack>
        );
    }

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("screens.profile_editor.finalize_my_signup"),
                    onPress: next,
                    isValid: isValid,
                    mode: "secondary",
                },
            ]}
        >
            <ListItem
                onPress={editCivilState}
                {...(!maritalStatus || !avsNumber
                    ? {
                          description: i18n.t("screens.profile_editor.civil_state.title"),
                          labels: [
                              {
                                  text: i18n.t("screens.profile_editor.please_complete"),
                                  mode: "miniCta",
                              },
                          ],
                          icon: "civil-state",
                      }
                    : {
                          title: i18n.t("screens.profile_editor.civil_state.title"),
                          description: i18n.t(`user.${maritalStatus}`),
                          icon: "check-circle-heavy",
                          iconColor: colors.secondary,
                      })}
            />
            {isPartnerAndChildrenDataRequired && (
                <ListItem
                    onPress={editChildren}
                    {...(hasChildren == null
                        ? {
                              description: i18n.t("screens.profile_editor.children.title"),
                              labels: [
                                  {
                                      text: i18n.t("screens.profile_editor.please_complete"),
                                      mode: "miniCta",
                                  },
                              ],
                              icon: "children",
                          }
                        : {
                              title: i18n.t("screens.profile_editor.children.title"),
                              description: i18n.t("screens.profile_editor.children_number", {
                                  count: children.length,
                              }),
                              icon: "check-circle-heavy",
                              iconColor: colors.secondary,
                          })}
                />
            )}
            <ListItem
                onPress={editBankAccount}
                {...(!bankAccount
                    ? {
                          description: i18n.t("screens.profile_editor.bank_account.title"),
                          labels: [
                              {
                                  text: i18n.t("screens.profile_editor.please_complete"),
                                  mode: "miniCta",
                              },
                          ],
                          icon: "dollar-square",
                      }
                    : {
                          title: i18n.t("screens.profile_editor.bank_account.title"),
                          description: toIbanFormat(bankAccount.iban),
                          icon: "check-circle-heavy",
                          iconColor: colors.secondary,
                      })}
            />
            {isFiscalAttestationRequired && (
                <ListItem
                    onPress={editFiscalAttestation}
                    {...(hasFiscalAttestation == null
                        ? {
                              description: i18n.t(
                                  "screens.profile_editor.fiscal_attestation.title",
                              ),
                              labels: [
                                  {
                                      text: i18n.t("screens.profile_editor.please_complete"),
                                      mode: "miniCta",
                                  },
                              ],
                              icon: "file-lines",
                          }
                        : {
                              title: i18n.t("screens.profile_editor.fiscal_attestation.title"),
                              description: hasFiscalAttestation ? i18n.t("yes") : i18n.t("no"),
                              icon: "check-circle-heavy",
                              iconColor: colors.secondary,
                          })}
                />
            )}
            {newProfileId && (
                <ListItem
                    onPress={editPrivateEmail}
                    {...(!email
                        ? {
                              description: i18n.t("screens.profile_editor.private_email.title"),
                              labels: [
                                  {
                                      text: i18n.t("screens.profile_editor.please_complete"),
                                      mode: "miniCta",
                                  },
                              ],
                              icon: "private-email",
                          }
                        : {
                              title: i18n.t("screens.profile_editor.private_email.title"),
                              description: email,
                              icon: "check-circle-heavy",
                              iconColor: colors.secondary,
                          })}
                />
            )}
            {newProfileId && (
                <ListItem
                    onPress={editHomeAddress}
                    {...(!address
                        ? {
                              description: i18n.t("screens.profile_editor.home_address.title"),
                              labels: [
                                  {
                                      text: i18n.t("screens.profile_editor.please_complete"),
                                      mode: "miniCta",
                                  },
                              ],
                              icon: "house",
                          }
                        : {
                              title: i18n.t("screens.profile_editor.home_address.title"),
                              description: address.formatted,
                              icon: "check-circle-heavy",
                              iconColor: colors.secondary,
                          })}
                />
            )}
        </ScreenCard>
    );
};

const useStyles = () => {
    const { gridSize } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                headerActions: {
                    marginRight: gridSize / 2,
                },
            }),
        [gridSize],
    );
};

const toIbanFormat = (iban: string) =>
    iban
        .replace(/[^\dA-Z]/g, "")
        .replace(/(.{4})/g, "$1 ")
        .trim();

export default PostOnboardingProfileEditorScreen;
